import React, { useEffect, useState } from 'react';
import { loadIntegration } from '../lib/integrations';

export type ImageMetadataModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { mode: 'manual' | 'ai'; tags: string[]; description: string }) => void;
  imageUrl?: string;
};

const ImageMetadataModal: React.FC<ImageMetadataModalProps> = ({ open, onClose, onConfirm, imageUrl }) => {
  const [mode, setMode] = useState<'manual' | 'ai'>('manual');
  const [tagsInput, setTagsInput] = useState('');
  const [description, setDescription] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setMode('manual');
      setTagsInput('');
      setDescription('');
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-purple-600 to-blue-600 text-white font-bold">✦</span>
            <h2 className="font-semibold text-gray-900">Add Tags and Meta Description</h2>
          </div>
          <button onClick={onClose} className="rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50">Close</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="mode" checked={mode==='manual'} onChange={() => setMode('manual')} />
              <span className="text-gray-800">Manual</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="radio" name="mode" checked={mode==='ai'} onChange={() => setMode('ai')} />
              <span className="text-gray-800">Let AI suggest (coming soon)</span>
            </label>
            {mode==='ai' && (
              <div className="ml-auto inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setAiError(null);
                    if (!imageUrl) { setAiError('No image URL available'); return; }
                    setAnalyzing(true);
                    try {
                      const cfg = await loadIntegration('mistral');
                      const key = (cfg && typeof (cfg as any).apiKey === 'string')
                        ? (cfg as any).apiKey
                        : (cfg && typeof (cfg as any).apikey === 'string')
                          ? (cfg as any).apikey
                          : '';
                      if (!key) { setAiError('Mistral API key not set in Integrations'); setAnalyzing(false); return; }
                      const model = (cfg && typeof (cfg as any).model === 'string' && (cfg as any).model) || 'pixtral-12b-2409';
                      let imgSource = imageUrl;
                      try {
                        const r = await fetch(imageUrl, { cache: 'no-store' });
                        if (r.ok) {
                          const blob = await r.blob();
                          const dataUrl: string = await new Promise((resolve, reject) => {
                            const fr = new FileReader();
                            fr.onerror = () => reject(new Error('read error'));
                            fr.onloadend = () => resolve(String(fr.result));
                            fr.readAsDataURL(blob);
                          });
                          imgSource = dataUrl;
                        }
                      } catch {}
                      const res = await fetch('https://api.mistral.ai/v1/chat/completions', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${key}` },
                        body: JSON.stringify({
                          model,
                          messages: [
                            { role: 'system', content: 'You analyze an image and return concise SEO tags (max 8) and a 1-2 sentence meta description. Respond ONLY as compact JSON: {"tags": string[], "description": string}.' },
                            { role: 'user', content: [
                              { type: 'text', text: 'Analyze this image and respond as JSON. Do not include any other text.' },
                              { type: 'image_url', image_url: { url: imgSource } }
                            ] }
                          ],
                          temperature: 0.2,
                          max_tokens: 256,
                        }),
                      });
                      if (!res.ok) {
                        const status = res.status;
                        const ct = res.headers.get('content-type') || '';
                        let msg = '';
                        if (ct.toLowerCase().includes('application/json')) {
                          try {
                            const j = await res.json();
                            if (j?.error) {
                              if (typeof j.error === 'string') msg = j.error;
                              else if (typeof j.error?.message === 'string') msg = j.error.message;
                            }
                          } catch {}
                        }
                        if (!msg) {
                          try {
                            const t = await res.text();
                            msg = t?.slice(0, 300) || '';
                          } catch {}
                        }
                        msg = msg || 'Request was rejected by Mistral';
                        throw new Error(`Mistral: ${msg} (HTTP ${status})`);
                      }
                      const data = await res.json();
                      const content = data?.choices?.[0]?.message?.content || '';
                      if (typeof content !== 'string') { setAiError('Unexpected AI response'); setAnalyzing(false); return; }
                      let parsed: any = null;
                      try { parsed = JSON.parse(content); } catch (e) {
                        // Try to extract JSON substring
                        const m = content.match(/\{[\s\S]*\}/);
                        if (m) { try { parsed = JSON.parse(m[0]); } catch {} }
                      }
                      if (!parsed || !Array.isArray(parsed.tags) || typeof parsed.description !== 'string') {
                        setAiError('AI did not return valid JSON');
                      } else {
                        setTagsInput(parsed.tags.join(', '));
                        setDescription(parsed.description);
                      }
                    } catch (e: any) {
                      setAiError(e?.message || String(e));
                    } finally {
                      setAnalyzing(false);
                    }
                  }}
                  className={`inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium ${analyzing ? 'border border-purple-200 text-purple-700' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                  disabled={analyzing}
                >
                  {analyzing ? (
                    <span className="inline-flex items-center gap-2"><span className="h-4 w-4 rounded-full border-2 border-purple-300 border-t-purple-700 animate-spin" /> Analyzing</span>
                  ) : (
                    'Analyze'
                  )}
                </button>
              </div>
            )}
          </div>
          {aiError && <div className="text-xs text-red-600 border border-red-200 bg-red-50 px-2 py-1 rounded">{aiError}</div>}
          <div className="grid gap-3">
            <label className="text-sm text-gray-700">Tags (comma-separated)</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
              type="text"
              placeholder="product, hero, banner"
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              disabled={mode==='ai'}
            />
            {/* Live tag chips */}
            <div className="flex flex-wrap gap-2">
              {tagsInput.split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
                <span key={`${t}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 text-xs">
                  <span className="h-1.5 w-1.5 rounded-full bg-purple-600" />
                  {t}
                </span>
              ))}
              {tagsInput.split(',').map(t => t.trim()).filter(Boolean).length === 0 && (
                <span className="text-xs text-gray-500">No tags yet</span>
              )}
            </div>
          </div>
          <div className="grid gap-3">
            <label className="text-sm text-gray-700">{mode==='ai' ? 'AI Meta Description (coming soon)' : 'Meta Description'}</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
              placeholder="A short descriptive summary for SEO and sharing"
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={mode==='ai'}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50">Skip</button>
            <button
              onClick={() => onConfirm({ mode, tags: tagsInput.split(',').map(t=>t.trim()).filter(Boolean), description })}
              disabled={tagsInput.split(',').map(t=>t.trim()).filter(Boolean).length === 0 || description.trim() === ''}
              className={`inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium ${ (tagsInput.split(',').map(t=>t.trim()).filter(Boolean).length === 0 || description.trim() === '') ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageMetadataModal;
